import React from 'react';
import classNames from 'classnames';
import { format, isValid } from 'date-fns';
import { de } from 'date-fns/locale';
import { FormattedMessage, injectIntl } from 'react-intl';

import { Button, EButtonType } from '@repo/apg-ui';
import { isString } from '../../utils/variableEvaluation';
import styles from './CampaignRow.module.scss';
import { ReactComponent as RightArrow } from '../../assets/icons/arrow_right.svg';
import { ReactComponent as RedPlus } from '../../assets/icons/redPlus.svg';
import { ReactComponent as GreenEye } from '../../assets/icons/greenEye.svg';
import { ReactComponent as BuyIcon } from '../../assets/icons/buy.svg';
import { ReactComponent as GoTo } from '../../assets/icons/goTo.svg';
import { ReactComponent as RedX } from '../../assets/icons/redX.svg';
import { ReactComponent as Duplicate } from '../../assets/icons/duplicate.svg';
import { isProd, isStage } from '../../constants/config';
import { useNavigate } from 'react-router';

export interface Props {
  product: any;
  index: any;
  basketId?: string;
  moreContent: any;
  orderCompletedPage?: boolean;
  intl: any;
}

const CampaignRow = ({ product, intl, orderCompletedPage }: Props) => {
  const navigate = useNavigate();
  // -1: not possible (red cross icon)
  // 0: possible, but no order yet created (red plus)
  // >0: already order created, the number is the id of the order (to get the order data and show them on a seperate page)
  // -2: finally the new value, special for the traffic products, here you must link to the special "traffic info page" - the one I have shot the screen of in the picture above

  const getCreationOrderButtons = () => {
    const buttons = [];
    const creationOrder = +product.creationorder;

    if (creationOrder === -1) {
    } else if (creationOrder === 0) {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(
              product.type === 'digital'
                ? `/campaign/${product.number}/creation-order-new-digital`
                : `/campaign/${product.number}/creation-order-new`,
            );
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderIssue',
            defaultMessage: 'Gestaltungsauftrag erteilen',
          })}
        >
          <RedPlus />
        </Button>,
      );
    } else if (creationOrder === -2) {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${product.number}/production-order-railposters`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderIssue',
            defaultMessage: 'Gestaltungsauftrag erteilen',
          })}
        >
          <RedPlus />
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(
              product.type === 'digital'
                ? `/campaign/${product.number}/creation-order-digital/${product.creationorder}`
                : `/campaign/${product.number}/creation-order/${product.creationorder}`,
            );
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.designOrderView',
            defaultMessage: 'Gestaltungsauftrag ansehen',
          })}
        >
          <GreenEye />
        </Button>,
      );
    }

    const url = isProd
      ? intl.formatMessage({
          id: 'ec.campaignRow.designTemplatesProdLink',
          defaultMessage: 'https://apgsga.ch/de/vorlagen-und-spezifikationen/',
        })
      : isStage
        ? intl.formatMessage({
            id: 'ec.campaignRow.designTemplatesStageLink',
            defaultMessage:
              'https://apg-website-2021-private-cloud-stage.apg.divio.cloud/de/vorlagen-und-spezifikationen/',
          })
        : intl.formatMessage({
            id: 'ec.campaignRow.designTemplatesDevLink',
            defaultMessage:
              'https://apgwebsite2021privatecloud-dev-8fd10b4.apg.divio.cloud/de/vorlagen-und-spezifikationen/',
          });
    buttons.push(
      <Button
        className={classNames(`${styles.productRowButton} me-3`, { active: false })}
        buttonType={EButtonType.BasicLink}
        onClick={() => {
          navigate(url);
        }}
        title={intl.formatMessage({
          id: 'ec.campaignRow.designTemplates',
          defaultMessage: 'Gestaltungsvorlagen',
        })}
      >
        <BuyIcon />
      </Button>,
    );

    return buttons;
  };

  const getProductionOrderButtons = () => {
    const buttons = [];

    if (product.productionorder == -1) {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} ${styles.gestaltungSecond} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          isDisabled={true}
        >
          <RedX />
        </Button>,
      );
    } else if (product.productionorder == 0) {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} ${styles.gestaltungSecond} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${product.number}/production-order-new`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderIssue',
            defaultMessage: 'Druckauftrag erteilen',
          })}
        >
          <RedPlus />
        </Button>,
      );
    } else if (product.productionorder == '-2') {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${product.number}/production-order-railposters`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderIssue',
            defaultMessage: 'Druckauftrag erteilen',
          })}
        >
          <RedPlus />
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          onClick={() => {
            navigate(`/campaign/${product.number}/production-order/${product.productionorder}`);
          }}
          title={intl.formatMessage({
            id: 'ec.campaignRow.printOrderView',
            defaultMessage: 'Druckauftrag ansehen',
          })}
        >
          <GreenEye />
        </Button>,
      );
    }

    return buttons;
  };

  const getUploadButtons = () => {
    const buttons = [];
    const { directupload } = product;

    if (directupload && isString(directupload) && directupload.toLowerCase().includes('https')) {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          href={directupload}
          title={intl.formatMessage({
            id: 'ec.campaignRow.spotUpload',
            defaultMessage: 'Spot upload',
          })}
        >
          <GoTo />
        </Button>,
      );
    } else {
      buttons.push(
        <Button
          className={classNames(`${styles.productRowButton} me-3`, { active: false })}
          buttonType={EButtonType.BasicLink}
          isDisabled
        >
          <RedX />
        </Button>,
      );
    }

    return buttons;
  };

  return (
    <div className={styles.productCampaignRow}>
      <div className={classNames(styles.productRowCampaignColumn)}>{product.number}</div>

      <div className={styles.productRowCampaignColumn}>{product.name}</div>

      <div className={classNames(`${styles.productRowCampaignColumn} text-wrap`)}>
        <FormattedMessage id='ec.campaignRow.Ab' defaultMessage='Ab' />{' '}
        {isValid(Date.parse(product.startdate))
          ? format(Date.parse(product.startdate), 'dd-MM-yyyy', { locale: de })
          : product.startdate}
      </div>

      <div className={styles.productRowCampaignColumn}>{product.formats}</div>

      <div
        className={classNames(styles.productRowCampaignColumn, {
          nonJ3: !orderCompletedPage,
        })}
      >
        <div className='d-flex flex-row'>{getCreationOrderButtons()}</div>
      </div>

      <div
        className={classNames(styles.productRowCampaignColumn, {
          nonJ3: !orderCompletedPage,
        })}
      >
        <div className='d-flex flex-row'>{getProductionOrderButtons()}</div>
      </div>

      <div
        className={classNames(styles.productRowCampaignColumn, {
          nonJ3: !orderCompletedPage,
        })}
      >
        <div className='d-flex flex-row'>{getUploadButtons()}</div>
      </div>

      <div className={classNames(styles.productRowCampaignColumn)}>
        <Button
          onClick={() => {
            navigate(`/journey3/${product.basket.id}/${product.number}`);
          }}
          className={`${styles.productRowButton} copyCampaignButton`}
          title={intl.formatMessage({
            id: 'ec.components.buttons.journey03.copyCampaign',
            defaultMessage: 'Kampagne nochmals buchen',
          })}
        >
          <Duplicate />
        </Button>
      </div>

      <div className={styles.productRowCampaignColumn}>
        <Button
          onClick={() => {
            navigate(`/campaign/${product.number}`);
          }}
        >
          <RightArrow />
        </Button>
      </div>
    </div>
  );
};

export default injectIntl(CampaignRow);
